import { Button, Loading, RowInput } from '@affine/component';
import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import { GroupRow, PersonRow } from '@affine/core/components/identity-row';
import {
  PermissionRoleMenu,
  type PermissionRoleMenuOption,
} from '@affine/core/modules/permissions/components/permission-role-menu';
import { WorkspaceMemberStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { DeleteIcon, PlusIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { debounce } from 'lodash-es';
import {
  type CompositionEventHandler,
  type MutableRefObject,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { Member } from '../entities/members';
import { MemberSearchService } from '../services/member-search';
import { identityRow } from './permission-access-list.css';
import * as styles from './permission-invite-picker.css';

export interface PermissionInviteSearchAvailabilityOptions {
  backendRequired?: boolean;
  searchFailed?: boolean;
  unavailableMessage: string;
}

export function usePermissionInviteSearchAvailability({
  backendRequired = true,
  searchFailed = false,
  unavailableMessage,
}: PermissionInviteSearchAvailabilityOptions) {
  const connectivityStatus = useConnectivityStatus();
  const liveConnectivityUnavailable = useUiOfflineSignal(
    backendRequired && connectivityStatus.offline
  );
  const searchUnavailable = liveConnectivityUnavailable || searchFailed;

  return useMemo(
    () => ({
      searchUnavailable,
      searchUnavailableMessage: searchUnavailable ? unavailableMessage : null,
    }),
    [searchUnavailable, unavailableMessage]
  );
}

export interface PermissionInvitePickerProps<Role extends string> {
  title: string;
  compact?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  role?: Role;
  roleOptions?: readonly PermissionRoleMenuOption<Role>[];
  onRoleSelect?: (role: Role) => void;
  roleSlot?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  inputTestId?: string;
  inputDisabled?: boolean;
  roleDisabled?: boolean;
  autoFocus?: boolean;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputCompositionStart?: CompositionEventHandler<HTMLInputElement>;
  onInputCompositionEnd?: CompositionEventHandler<HTMLInputElement>;
  offlineMessage?: string | null;
  children?: ReactNode;
}

export function PermissionInvitePicker<Role extends string>({
  title,
  compact = false,
  value,
  onChange,
  placeholder,
  role,
  roleOptions,
  onRoleSelect,
  roleSlot,
  inputRef,
  inputTestId,
  inputDisabled = false,
  roleDisabled = false,
  autoFocus = false,
  onInputFocus,
  onInputBlur,
  onInputCompositionStart,
  onInputCompositionEnd,
  offlineMessage = null,
  children,
}: PermissionInvitePickerProps<Role>) {
  const labelId = useId();
  const roleControl =
    roleSlot !== undefined ? (
      roleSlot
    ) : role && roleOptions && onRoleSelect ? (
      <PermissionRoleMenu
        role={role}
        options={roleOptions}
        onSelect={onRoleSelect}
        className={styles.roleButton}
        disabled={roleDisabled}
        testId={inputTestId ? `${inputTestId}-role` : undefined}
      />
    ) : null;

  return (
    <div className={compact ? styles.compactPicker : styles.pickerSection}>
      {!compact ? (
        <div className={styles.pickerHeader}>
          <PlusIcon className={styles.pickerIcon} />
          <span id={labelId}>{title}</span>
        </div>
      ) : null}
      <div className={styles.pickerInputRow}>
        <RowInput
          data-testid={inputTestId}
          aria-labelledby={compact ? undefined : labelId}
          aria-label={compact ? placeholder : undefined}
          ref={inputRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={styles.pickerInput}
          disabled={inputDisabled}
          autoFocus={autoFocus}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onCompositionStart={onInputCompositionStart}
          onCompositionEnd={onInputCompositionEnd}
        />
        {!compact || value.trim() ? roleControl : null}
      </div>
      {offlineMessage ? (
        <div role="status" className={styles.offlineNotice}>
          {offlineMessage}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export interface PermissionMemberRowProps {
  user: {
    id: string;
    name: string;
    email?: string;
    avatarUrl?: string | null;
  };
  roleSelector?: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  dataTestId?: string;
  onRemove?: () => void;
  removeDisabled?: boolean;
}

export function PermissionMemberRow({
  user,
  roleSelector,
  subtitle,
  badges,
  dataTestId,
  onRemove,
  removeDisabled = false,
}: PermissionMemberRowProps) {
  const t = useI18n();
  return (
    <PersonRow
      dataTestId={dataTestId}
      person={user}
      variant="row"
      className={identityRow}
      showTooltips
      subtitle={subtitle}
      badges={badges}
      actions={
        <>
          {roleSelector}
          {onRemove ? (
            <Button
              variant="plain"
              className={styles.removeButton}
              aria-label={`${t['Remove']()} ${user.name || user.email}`}
              disabled={removeDisabled}
              data-testid="remove-member-button"
              onClick={event => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <DeleteIcon />
            </Button>
          ) : null}
        </>
      }
    />
  );
}

export interface PermissionGroupRowProps {
  groupName: string;
  description?: ReactNode;
  /** Replaces the group mark, e.g. for a workspace Mate. */
  icon?: ReactNode;
  roleSlot?: ReactNode;
  dataTestId?: string;
  onRemove?: () => void;
  removeDisabled?: boolean;
  removeTestId?: string;
}

export function PermissionGroupRow({
  groupName,
  description,
  icon,
  roleSlot,
  dataTestId,
  onRemove,
  removeDisabled = false,
  removeTestId,
}: PermissionGroupRowProps) {
  const t = useI18n();
  return (
    <GroupRow
      dataTestId={dataTestId}
      name={groupName}
      description={description}
      icon={icon}
      variant="row"
      className={identityRow}
      actions={
        <>
          {roleSlot}
          {onRemove ? (
            <Button
              variant="plain"
              className={styles.removeButton}
              aria-label={`${t['Remove']()} ${groupName}`}
              disabled={removeDisabled}
              data-testid={removeTestId}
              onClick={event => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <DeleteIcon />
            </Button>
          ) : null}
        </>
      }
    />
  );
}

export interface PermissionInviteMemberOption {
  id: string;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  status?: WorkspaceMemberStatus | null;
}

export interface PermissionMemberInviteSearchProps<
  Role extends string,
  MemberOption extends PermissionInviteMemberOption = Member,
> {
  title: string;
  compact?: boolean;
  placeholder: string;
  noResultsText: string;
  offlineMessage?: string | null;
  roleOptions?: readonly PermissionRoleMenuOption<Role>[];
  defaultRole?: Role;
  roleSlot?: ReactNode;
  existingUserIds?: readonly string[];
  disabled: boolean;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onSelectMember: (
    member: MemberOption,
    role: Role | undefined
  ) => void | Promise<void>;
  onSelectError?: (error: unknown) => void;
  onVisibleMembersChange?: (members: MemberOption[]) => void;
  acceptedOnly?: boolean;
  maxVisibleItems?: number;
  showInitialResults?: boolean;
  preserveResultsWhileSearching?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  inputTestId?: string;
  autoFocus?: boolean;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputCompositionStart?: CompositionEventHandler<HTMLInputElement>;
  onInputCompositionEnd?: CompositionEventHandler<HTMLInputElement>;
  deferSearchWhileComposing?: boolean;
  children?: ReactNode;
}

export function PermissionMemberInviteSearch<
  Role extends string,
  MemberOption extends PermissionInviteMemberOption = Member,
>({
  title,
  compact = false,
  placeholder,
  noResultsText,
  offlineMessage = null,
  roleOptions,
  defaultRole,
  roleSlot,
  existingUserIds = [],
  disabled,
  searchText,
  onSearchTextChange,
  onSelectMember,
  onSelectError,
  onVisibleMembersChange,
  acceptedOnly = true,
  maxVisibleItems,
  showInitialResults = false,
  preserveResultsWhileSearching = false,
  inputRef,
  inputTestId,
  autoFocus = false,
  onInputFocus,
  onInputBlur,
  onInputCompositionStart,
  onInputCompositionEnd,
  deferSearchWhileComposing = false,
  children,
}: PermissionMemberInviteSearchProps<Role, MemberOption>) {
  const memberSearchService = useService(MemberSearchService);
  const memberSearchSession = useMemo(
    () => memberSearchService.createSession(),
    [memberSearchService]
  );
  const internalInputRef = useRef<HTMLInputElement>(null);
  const composingRef = useRef(false);
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(
    defaultRole
  );
  const [isSelecting, setIsSelecting] = useState(false);

  useEffect(() => {
    return () => {
      memberSearchSession.dispose();
    };
  }, [memberSearchSession]);

  useEffect(() => {
    if (
      defaultRole !== undefined &&
      roleOptions &&
      !roleOptions.some(option => option.role === selectedRole)
    ) {
      setSelectedRole(defaultRole);
    }
  }, [defaultRole, roleOptions, selectedRole]);

  useEffect(() => {
    memberSearchSession.reset();
    if (!disabled && showInitialResults) {
      memberSearchSession.loadMore();
    }
  }, [disabled, memberSearchSession, showInitialResults]);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => memberSearchSession.search(value), 300),
    [memberSearchSession]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const searchResults = useLiveData(memberSearchSession.result$);
  const isSearching = useLiveData(memberSearchSession.isLoading$);
  const searchError = useLiveData(memberSearchSession.error$);
  const existingUserIdSet = useMemo(
    () => new Set(existingUserIds),
    [existingUserIds]
  );
  const availableMembers = useMemo(() => {
    return (searchResults as unknown as MemberOption[]).filter(member => {
      if (acceptedOnly && member.status !== WorkspaceMemberStatus.Accepted) {
        return false;
      }
      return !existingUserIdSet.has(member.id);
    });
  }, [acceptedOnly, existingUserIdSet, searchResults]);

  useEffect(() => {
    onVisibleMembersChange?.(availableMembers);
  }, [availableMembers, onVisibleMembersChange]);

  const handleSearchChange = useCallback(
    (value: string) => {
      onSearchTextChange(value);
      if (!disabled && (!deferSearchWhileComposing || !composingRef.current)) {
        debouncedSearch(value);
      }
    },
    [debouncedSearch, deferSearchWhileComposing, disabled, onSearchTextChange]
  );

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      internalInputRef.current = node;
      if (!inputRef) {
        return;
      }
      if (typeof inputRef === 'function') {
        inputRef(node);
        return;
      }
      (inputRef as MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [inputRef]
  );

  const handleCompositionStart: CompositionEventHandler<HTMLInputElement> =
    useCallback(
      event => {
        composingRef.current = true;
        onInputCompositionStart?.(event);
      },
      [onInputCompositionStart]
    );

  const handleCompositionEnd: CompositionEventHandler<HTMLInputElement> =
    useCallback(
      event => {
        composingRef.current = false;
        if (!disabled && deferSearchWhileComposing) {
          debouncedSearch(event.currentTarget.value);
        }
        onInputCompositionEnd?.(event);
      },
      [
        debouncedSearch,
        deferSearchWhileComposing,
        disabled,
        onInputCompositionEnd,
      ]
    );

  const handleSelectMember = useCallback(
    async (member: MemberOption) => {
      if (disabled || isSelecting) {
        return;
      }
      setIsSelecting(true);
      try {
        await onSelectMember(member, selectedRole);
        onSearchTextChange('');
        memberSearchSession.reset();
      } catch (error) {
        onSelectError?.(error);
      } finally {
        setIsSelecting(false);
        internalInputRef.current?.focus();
      }
    },
    [
      disabled,
      isSelecting,
      memberSearchSession,
      onSearchTextChange,
      onSelectError,
      onSelectMember,
      selectedRole,
    ]
  );

  return (
    <PermissionMemberInvitePickerView
      title={title}
      compact={compact}
      placeholder={placeholder}
      noResultsText={noResultsText}
      offlineMessage={disabled || searchError ? offlineMessage : null}
      members={availableMembers}
      isSearching={isSearching}
      disabled={disabled}
      searchText={searchText}
      onSearchTextChange={handleSearchChange}
      onSelectMember={handleSelectMember}
      selectedRole={selectedRole}
      roleOptions={roleOptions}
      onRoleSelect={setSelectedRole}
      roleSlot={roleSlot}
      inputRef={setInputRef}
      inputTestId={inputTestId}
      roleDisabled={disabled}
      autoFocus={autoFocus}
      onInputFocus={onInputFocus}
      onInputBlur={onInputBlur}
      onInputCompositionStart={handleCompositionStart}
      onInputCompositionEnd={handleCompositionEnd}
      maxVisibleItems={maxVisibleItems}
      showInitialResults={showInitialResults}
      preserveResultsWhileSearching={preserveResultsWhileSearching}
    >
      {children}
    </PermissionMemberInvitePickerView>
  );
}

export interface PermissionMemberInvitePickerViewProps<
  Role extends string,
  MemberOption extends PermissionInviteMemberOption,
> {
  title: string;
  compact?: boolean;
  placeholder: string;
  noResultsText: string;
  offlineMessage?: string | null;
  members: readonly MemberOption[];
  isSearching: boolean;
  disabled: boolean;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onSelectMember: (member: MemberOption) => void | Promise<void>;
  roleOptions?: readonly PermissionRoleMenuOption<Role>[];
  selectedRole?: Role;
  onRoleSelect?: (role: Role) => void;
  roleSlot?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  inputTestId?: string;
  roleDisabled?: boolean;
  autoFocus?: boolean;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputCompositionStart?: CompositionEventHandler<HTMLInputElement>;
  onInputCompositionEnd?: CompositionEventHandler<HTMLInputElement>;
  maxVisibleItems?: number;
  showInitialResults?: boolean;
  preserveResultsWhileSearching?: boolean;
  children?: ReactNode;
}

export function PermissionMemberInvitePickerView<
  Role extends string,
  MemberOption extends PermissionInviteMemberOption,
>({
  title,
  compact = false,
  placeholder,
  noResultsText,
  offlineMessage = null,
  members,
  isSearching,
  disabled,
  searchText,
  onSearchTextChange,
  onSelectMember,
  roleOptions,
  selectedRole,
  onRoleSelect,
  roleSlot,
  inputRef,
  inputTestId,
  roleDisabled = disabled,
  autoFocus = false,
  onInputFocus,
  onInputBlur,
  onInputCompositionStart,
  onInputCompositionEnd,
  maxVisibleItems = 5,
  showInitialResults = false,
  preserveResultsWhileSearching = false,
  children,
}: PermissionMemberInvitePickerViewProps<Role, MemberOption>) {
  const [lastNonEmptyMembers, setLastNonEmptyMembers] = useState<
    readonly MemberOption[]
  >([]);

  useEffect(() => {
    if (members.length > 0) {
      setLastNonEmptyMembers(members);
    }
  }, [members]);

  const shouldShowResults =
    !disabled && (showInitialResults || searchText.length > 0);
  const visibleMembers =
    preserveResultsWhileSearching &&
    isSearching &&
    members.length === 0 &&
    lastNonEmptyMembers.length > 0
      ? lastNonEmptyMembers
      : members;

  return (
    <PermissionInvitePicker
      title={title}
      compact={compact}
      value={searchText}
      onChange={onSearchTextChange}
      placeholder={placeholder}
      role={selectedRole}
      roleOptions={roleOptions}
      onRoleSelect={onRoleSelect}
      roleSlot={roleSlot}
      inputRef={inputRef}
      inputTestId={inputTestId}
      roleDisabled={roleDisabled}
      autoFocus={autoFocus}
      onInputFocus={onInputFocus}
      onInputBlur={onInputBlur}
      onInputCompositionStart={onInputCompositionStart}
      onInputCompositionEnd={onInputCompositionEnd}
      inputDisabled={disabled}
      offlineMessage={offlineMessage}
    >
      {shouldShowResults && (
        <div className={styles.searchResults}>
          {isSearching && visibleMembers.length === 0 ? (
            <div className={styles.searchLoading}>
              <Loading />
            </div>
          ) : visibleMembers.length === 0 ? (
            <div className={styles.noResults}>{noResultsText}</div>
          ) : (
            visibleMembers.slice(0, maxVisibleItems).map(member => (
              <div
                key={member.id}
                className={styles.searchResultItem}
                data-testid={
                  inputTestId ? `${inputTestId}-result-${member.id}` : undefined
                }
                onClick={() => {
                  void onSelectMember(member);
                }}
              >
                <PersonRow
                  person={{
                    id: member.id,
                    name: member.name ?? '',
                    email: member.email ?? undefined,
                    avatarUrl: member.avatarUrl ?? undefined,
                  }}
                  variant="option"
                />
              </div>
            ))
          )}
        </div>
      )}
      {children}
    </PermissionInvitePicker>
  );
}

export interface PermissionInviteGroupOption {
  id: string;
  name: string;
  description?: string | null;
}

export interface PermissionGroupInviteSearchProps<
  Role extends string,
  GroupOption extends PermissionInviteGroupOption,
> {
  title: string;
  compact?: boolean;
  placeholder: string;
  noResultsText: string;
  offlineMessage?: string | null;
  showOfflineMessage?: boolean;
  roleOptions?: readonly PermissionRoleMenuOption<Role>[];
  defaultRole?: Role;
  roleSlot?: ReactNode;
  groups: readonly GroupOption[];
  assignedGroupIds?: ReadonlySet<string>;
  disabled: boolean;
  searchText: string;
  inputTestId?: string;
  onSearchTextChange: (value: string) => void;
  onSelectGroup: (
    group: GroupOption,
    role: Role | undefined
  ) => void | Promise<unknown>;
  getGroupDescription?: (
    group: GroupOption,
    role: Role | undefined
  ) => ReactNode;
  children?: ReactNode;
}

export function PermissionGroupInviteSearch<
  Role extends string,
  GroupOption extends PermissionInviteGroupOption,
>({
  title,
  compact = false,
  placeholder,
  noResultsText,
  offlineMessage = null,
  showOfflineMessage,
  roleOptions,
  defaultRole,
  roleSlot,
  groups,
  assignedGroupIds,
  disabled,
  searchText,
  inputTestId,
  onSearchTextChange,
  onSelectGroup,
  getGroupDescription,
  children,
}: PermissionGroupInviteSearchProps<Role, GroupOption>) {
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(
    defaultRole
  );

  useEffect(() => {
    if (
      defaultRole !== undefined &&
      roleOptions &&
      !roleOptions.some(option => option.role === selectedRole)
    ) {
      setSelectedRole(defaultRole);
    }
  }, [defaultRole, roleOptions, selectedRole]);

  const availableGroups = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return groups.filter(group => {
      if (assignedGroupIds?.has(group.id)) {
        return false;
      }
      if (!query) {
        return true;
      }
      return (
        group.name.toLowerCase().includes(query) ||
        group.description?.toLowerCase().includes(query)
      );
    });
  }, [assignedGroupIds, groups, searchText]);

  const handleSelectGroup = useCallback(
    (group: GroupOption) => {
      if (disabled) {
        return;
      }
      void Promise.resolve(onSelectGroup(group, selectedRole)).catch(
        console.error
      );
      onSearchTextChange('');
    },
    [disabled, onSearchTextChange, onSelectGroup, selectedRole]
  );

  return (
    <PermissionGroupInvitePickerView
      title={title}
      compact={compact}
      placeholder={placeholder}
      noResultsText={noResultsText}
      offlineMessage={(showOfflineMessage ?? disabled) ? offlineMessage : null}
      groups={availableGroups}
      disabled={disabled}
      searchText={searchText}
      inputTestId={inputTestId}
      onSearchTextChange={onSearchTextChange}
      onSelectGroup={handleSelectGroup}
      getGroupDescription={group =>
        getGroupDescription?.(group, selectedRole) ?? group.description
      }
      selectedRole={selectedRole}
      roleOptions={roleOptions}
      onRoleSelect={setSelectedRole}
      roleSlot={roleSlot}
      roleDisabled={disabled}
    >
      {children}
    </PermissionGroupInvitePickerView>
  );
}

export interface PermissionGroupInvitePickerViewProps<
  Role extends string,
  GroupOption extends PermissionInviteGroupOption,
> {
  title: string;
  compact?: boolean;
  placeholder: string;
  noResultsText: string;
  offlineMessage?: string | null;
  groups: readonly GroupOption[];
  isSearching?: boolean;
  disabled: boolean;
  searchText: string;
  inputTestId?: string;
  onSearchTextChange: (value: string) => void;
  onSelectGroup: (group: GroupOption) => void | Promise<unknown>;
  getGroupDescription?: (group: GroupOption) => ReactNode;
  roleOptions?: readonly PermissionRoleMenuOption<Role>[];
  selectedRole?: Role;
  onRoleSelect?: (role: Role) => void;
  roleSlot?: ReactNode;
  roleDisabled?: boolean;
  children?: ReactNode;
}

export function PermissionGroupInvitePickerView<
  Role extends string,
  GroupOption extends PermissionInviteGroupOption,
>({
  title,
  compact = false,
  placeholder,
  noResultsText,
  offlineMessage = null,
  groups,
  isSearching = false,
  disabled,
  searchText,
  inputTestId,
  onSearchTextChange,
  onSelectGroup,
  getGroupDescription,
  roleOptions,
  selectedRole,
  onRoleSelect,
  roleSlot,
  roleDisabled = disabled,
  children,
}: PermissionGroupInvitePickerViewProps<Role, GroupOption>) {
  return (
    <PermissionInvitePicker
      title={title}
      compact={compact}
      value={searchText}
      onChange={onSearchTextChange}
      placeholder={placeholder}
      inputTestId={inputTestId}
      role={selectedRole}
      roleOptions={roleOptions}
      onRoleSelect={onRoleSelect}
      roleSlot={roleSlot}
      roleDisabled={roleDisabled}
      inputDisabled={disabled}
      offlineMessage={offlineMessage}
    >
      {searchText && !disabled && (
        <div className={styles.searchResults}>
          {isSearching ? (
            <div className={styles.searchLoading}>
              <Loading />
            </div>
          ) : groups.length === 0 ? (
            <div className={styles.noResults}>{noResultsText}</div>
          ) : (
            groups.slice(0, 5).map(group => {
              return (
                <div
                  key={group.id}
                  className={styles.searchResultItem}
                  data-testid={
                    inputTestId
                      ? `${inputTestId}-result-${group.id}`
                      : undefined
                  }
                  onClick={() => {
                    void onSelectGroup(group);
                  }}
                >
                  <GroupRow
                    name={group.name}
                    description={
                      getGroupDescription?.(group) ?? group.description
                    }
                    variant="option"
                  />
                </div>
              );
            })
          )}
        </div>
      )}
      {children}
    </PermissionInvitePicker>
  );
}
